import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { MoreVertical, Trash2, Edit2, MessageSquare, Flag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAuth } from '@/hooks/useAuth';
import { useActivityComments } from '@/hooks/useActivityComments';
import { useTextModeration } from '@/hooks/useTextModeration';
import { MentionAutocomplete } from '@/components/MentionAutocomplete';
import { MentionText } from '@/components/MentionText';
import { AuthorHeader } from '@/components/AuthorHeader';
import { ReportButton } from '@/components/ReportButton';

interface ActivityCommentThreadProps {
  activityId: string;
  isOpen: boolean;
  onClose: () => void;
  activityDescription?: string;
}

export function ActivityCommentThread({ 
  activityId, 
  isOpen, 
  onClose,
  activityDescription 
}: ActivityCommentThreadProps) {
  const { user } = useAuth();
  const {
    comments,
    isLoading,
    addComment,
    updateComment,
    deleteComment,
    isAddingComment,
  } = useActivityComments(activityId);
  const { moderateText, isChecking: isModeratingText } = useTextModeration();

  const [newComment, setNewComment] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [replyingTo, setReplyingTo] = useState<{ id: string; username: string } | null>(null);

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    // Moderate content before posting
    const isApproved = await moderateText(newComment.trim());
    if (!isApproved) return;
    
    addComment(newComment.trim(), replyingTo?.id);
    setNewComment('');
    setReplyingTo(null);
  };

  const handleUpdateComment = async (commentId: string) => {
    if (!editingText.trim()) return;
    
    // Moderate content before updating
    const isApproved = await moderateText(editingText.trim());
    if (!isApproved) return;
    
    updateComment({ commentId, commentText: editingText });
    setEditingCommentId(null);
    setEditingText('');
  };

  const handleDeleteComment = (commentId: string) => {
    deleteComment(commentId);
    setDeleteConfirmId(null);
  };

  const startEditing = (commentId: string, currentText: string) => {
    setEditingCommentId(commentId);
    setEditingText(currentText);
  };

  const startReplying = (commentId: string, username: string) => {
    setReplyingTo({ id: commentId, username });
    setNewComment(`@${username} `);
  };

  const renderComment = (comment: any, depth = 0) => {
    const isOwnComment = comment.user_id === user?.id;
    const marginLeft = depth > 0 ? `${depth * 2}rem` : '0';

    return (
      <div key={comment.id} style={{ marginLeft }}>
        <div className="flex gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
          <Avatar className="w-8 h-8 flex-shrink-0">
            <AvatarImage src={comment.profiles?.image} />
            <AvatarFallback>
              {comment.profiles?.username?.[0]?.toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <AuthorHeader
                username={comment.profiles?.username || 'Unknown'}
                name={comment.profiles?.name}
                showFullFormat={false}
              />
              <span className="text-xs text-muted-foreground">
                {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
              </span>
            </div>

            {editingCommentId === comment.id ? (
              <div className="space-y-2">
                <MentionAutocomplete
                  value={editingText}
                  onChange={setEditingText}
                >
                  <Textarea
                    className="min-h-[80px]"
                    maxLength={2000}
                  />
                </MentionAutocomplete>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleUpdateComment(comment.id)}
                    disabled={!editingText.trim()}
                  >
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setEditingCommentId(null);
                      setEditingText('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <MentionText 
                  text={comment.comment_text}
                  className="text-sm whitespace-pre-wrap break-words"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => startReplying(comment.id, comment.profiles?.username || 'user')}
                  className="mt-1 h-7 text-xs"
                >
                  Reply
                </Button>
              </>
            )}
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {isOwnComment ? (
                <>
                  <DropdownMenuItem onClick={() => startEditing(comment.id, comment.comment_text)}>
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setDeleteConfirmId(comment.id)}
                    className="text-destructive"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </>
              ) : (
                <DropdownMenuItem asChild>
                  <ReportButton
                    contentId={comment.id}
                    contentType="activity_comment"
                    contentPreview={comment.comment_text.substring(0, 100)}
                    variant="ghost"
                  />
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {comment.replies && comment.replies.length > 0 && (
          <div className="mt-2">
            {comment.replies.map((reply: any) => renderComment(reply, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Comments
            </DialogTitle>
            {activityDescription && (
              <p className="text-sm text-muted-foreground">{activityDescription}</p>
            )}
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-4 pr-2">
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2].map((i) => (
                  <div key={i} className="flex gap-3 animate-pulse">
                    <div className="w-8 h-8 bg-muted rounded-full" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-muted rounded w-1/4" />
                      <div className="h-12 bg-muted rounded" />
                    </div>
                  </div>
                ))}
              </div>
            ) : comments.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                No comments yet. Be the first to comment!
              </p>
            ) : (
              comments.map((comment) => renderComment(comment))
            )}
          </div>

          {/* Add Comment */}
          {user && (
            <div className="flex gap-3 pt-4 border-t mt-4">
              <Avatar className="h-8 w-8 flex-shrink-0">
                <AvatarImage src={user.user_metadata?.avatar_url} />
                <AvatarFallback>
                  {user.email?.charAt(0).toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>

              <div className="flex-1 space-y-2">
                <MentionAutocomplete
                  value={newComment}
                  onChange={setNewComment}
                >
                  <Textarea
                    placeholder="Write a comment..."
                    className="min-h-[60px] resize-none"
                    maxLength={2000}
                  />
                </MentionAutocomplete>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">
                    {newComment.length}/2000
                  </span>
                  <Button
                    size="sm"
                    onClick={handleAddComment}
                    disabled={!newComment.trim() || isAddingComment || isModeratingText}
                  >
                    {isAddingComment || isModeratingText ? (isModeratingText ? 'Checking...' : 'Posting...') : 'Post Comment'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!deleteConfirmId}
        onOpenChange={() => setDeleteConfirmId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Comment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this comment? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteConfirmId && handleDeleteComment(deleteConfirmId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}